import { cloneElement, type ComponentProps, type MouseEvent, type ReactElement } from 'react';
import { Focusable } from 'react-aria-components';
import { Tooltip } from '@heroui/react';

type TargetChild = ComponentProps<typeof Focusable>['children'];

/** Swallow activation of a button that is only aria-disabled (it is still in
 *  a form: a submit button must not submit). */
function blockActivation(e: MouseEvent): void {
  e.preventDefault();
  e.stopPropagation();
}

/**
 * A disabled `<button>` that still answers hover and focus, so its tooltip
 * can show: `aria-disabled` instead of `disabled`, its own handlers dropped
 * (as a native disabled button gets none) and activation blocked. The
 * `disabled:` Tailwind variant and the `:disabled` CSS rules also match
 * `[aria-disabled="true"]` (index.css), so it looks the same.
 */
export function softDisableButton(child: TargetChild): TargetChild {
  const props = child.props as Record<string, unknown>;
  // `disabled` is always removed below, so any truthy value must count:
  // a non-boolean one would otherwise turn the button back on.
  const disabled = Boolean(props.disabled);
  const next: Record<string, unknown> = { disabled: undefined };
  if (disabled) {
    for (const key of Object.keys(props)) {
      if (/^on[A-Z]/.test(key)) next[key] = undefined;
    }
    next['aria-disabled'] = true;
    next.onClick = blockActivation;
  }
  return cloneElement(child as ReactElement<Record<string, unknown>>, next) as TargetChild;
}

/**
 * Makes a native `<button>` / `<a>` the tooltip's trigger itself.
 *
 * HeroUI's `Tooltip.Trigger` renders its own focusable `div role="button"`,
 * so wrapping a real button in it gave every icon two Tab stops and a label
 * read twice (U7). `Focusable` instead merges the tooltip's trigger props and
 * ref into the one child element. HeroUI `<Button>` needs no wrapper at all:
 * put it directly inside `<Tooltip>`.
 *
 * A button keeps the same element whether or not it is disabled (see
 * softDisableButton): toggling `disabled` neither remounts it (focus and
 * entrance animations survive) nor wraps it in a second button.
 *
 * The child must be a DOM element (or forward its ref to one).
 */
export function TooltipTarget({ children }: { children: TargetChild }) {
  if (children.type === 'button') return <Focusable>{softDisableButton(children)}</Focusable>;
  const props = children.props as { disabled?: unknown; 'aria-label'?: unknown };
  if (props.disabled === true) {
    // A disabled field (input/select/textarea) gets no hover/focus events and
    // cannot become aria-disabled without turning editable: the wrapper is the
    // one Tab stop and carries the field's name.
    const label = typeof props['aria-label'] === 'string' ? props['aria-label'] : undefined;
    return <Tooltip.Trigger aria-label={label}>{children}</Tooltip.Trigger>;
  }
  return <Focusable>{children}</Focusable>;
}
